import { Dispatch, SetStateAction, useState, useEffect } from "react";
import { usePollingStatus } from "hooks/usePollingUntilSuccess";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import IconButton from "@mui/material/IconButton";
import { Typography } from "@mui/material";
import FileAdd from "assets/Images/file-add.png";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import { getInfoCarByImage } from "../MainBody/api";
import { useTranslation } from "react-i18next";
import { get } from "lodash";
import { useTheme } from "@mui/material/styles";
import useStyles from "./useStyles";
import "./styles.css";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MIN_FILE_SIZE = 50 * 1024;

const ImageUploadingFromLib = ({
  setDataFromImage,
  resetSelect,
  setIsLoadImg,
  isLoadImg,
  files,
  setFiles,
  description,
  type,
  layout,
  files2,
  setFiles2,
  disabled,
  isBack,
  setCarType,
}: {
  setDataFromImage: Function;
  resetSelect: Function;
  setIsLoadImg: Function;
  isLoadImg: boolean;
  files: any;
  setFiles: Function;
  description?: string;
  type: string;
  layout: string;
  files2: any;
  setFiles2: Function;
  disabled: boolean;
  isBack?: boolean;
  setCarType?: Dispatch<SetStateAction<string>>;
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const [jobId, setJobId] = useState<string | null>(null);
  const { dataPolling, error } = usePollingStatus(jobId);

  useEffect(() => {
    if (dataPolling?.data?.meta?.status === "PROCESSING") {
      setIsLoadImg(true);
    }
    if (dataPolling?.data?.meta?.status === "FAILED") {
      setIsLoadImg(false);
    }
    if (dataPolling?.status === 200) {
      setIsLoadImg(false);
      const getByImg = dataPolling?.data;
      const {
        companyName,
        versionName,
        manufactureYear,
        vehicleType,
        modelCode,
        barrelType,
        barrelTypeKey,
        carCompany,
        carModel,
        carVersion,
        year,
        numOfSeat,
        carType,
        carValue,
        minPrice,
        maxPrice,
      } = getByImg;
      if (vehicleType && setCarType) {
        setCarType(vehicleType);
        localStorage.setItem("type", vehicleType);
      }
      setJobId(null);
      setDataFromImage({
        predictedCarCompany: companyName ?? carCompany,
        predictedCarModel: versionName ?? carModel,
        predictedYear: manufactureYear ?? year,
        vehicleType,
        modelCode,
        barrelType,
        barrelTypeKey,
        predictedCarVersion: carVersion,
        numOfSeat,
        carType,
        carValue,
        minPrice,
        maxPrice,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataPolling]);

  useEffect(() => {
    if (error) setIsLoadImg(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const validateFileSize = (file: File) => {
    if (file.size < MIN_FILE_SIZE) {
      return {
        code: "file-too-small",
        message: t("upload.notification.file-too-small", {
          minSize: formatFileSize(MIN_FILE_SIZE),
          fileSize: formatFileSize(file.size),
        }),
      };
    }

    return null;
  };

  const validateFileExtension = (file: File) => {
    const allowedExtensions = [
      ".jpg",
      ".jpeg",
      ".png",
      ".PNG",
      ".JPG",
      ".JPEG",
    ];
    const fileName = file.name;
    const hasValidExtension = allowedExtensions.some((ext) =>
      fileName.endsWith(ext),
    );

    if (!hasValidExtension) {
      return {
        code: "file-invalid-type",
        message: t("upload.notification.invalid-type"),
      };
    }

    return null;
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpeg": [".jpg", ".jpeg", ".JPG", ".JPEG"],
      "image/png": [".png", ".PNG"],
    },
    maxFiles: 1,
    maxSize: MAX_FILE_SIZE,
    validator: (file) => {
      const sizeError = validateFileSize(file);
      if (sizeError) return sizeError;

      return validateFileExtension(file);
    },
    onDrop: async (acceptedFiles, rejectedFiles) => {
      if (disabled) {
        return;
      }

      if (rejectedFiles.length > 0) {
        const fileRejectionErrors = rejectedFiles[0].errors.map((error) => {
          switch (error.code) {
            case "file-invalid-type": {
              return t("upload.notification.invalid-type");
            }

            case "file-too-large": {
              return t("upload.notification.file-too-large", {
                maxSize: formatFileSize(MAX_FILE_SIZE),
                fileSize: formatFileSize(rejectedFiles[0].file.size),
              });
            }

            case "file-too-small": {
              return error.message;
            }

            case "too-many-files":
              return t("upload.notification.too-many-files");
            default:
              return t("upload.notification.server-error");
          }
        });

        toast.error(fileRejectionErrors[0], {
          position: "top-center",
          autoClose: 3000,
        });
        return;
      }

      setFiles(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          }),
        ),
      );
      resetSelect();

      if (!isBack && files2?.length > 0) {
        setFiles2([]);
        return;
      }

      if (type === "two" && files.length === 0 && files2.length === 0) return;

      const formData = new FormData();
      const file = acceptedFiles[0];
      formData.append("img", file);
      if (files2?.length > 0) formData.append("img", files2[0]);

      setIsLoadImg(true);
      try {
        const getByImgApi = await getInfoCarByImage(formData);
        if (getByImgApi?.meta?.jobId) {
          setJobId(getByImgApi?.meta?.jobId);
          setIsLoadImg(true);
          return;
        }
        const getByImg = getByImgApi?.codebookPredictResult;
        const {
          carCompany,
          carModel,
          carVersion,
          manufactureYear,
          vehicleInspectionOcrId,
          vehicleTypeKey,
          numOfSeat,
          carType,
          vehicleType,
          barrelType,
          barrelTypeKey,
        } = getByImg;

        if ((vehicleTypeKey || vehicleType) && setCarType) {
          setCarType(vehicleTypeKey ?? vehicleType);
          localStorage.setItem("type", vehicleTypeKey ?? vehicleType);
        }

        setDataFromImage({
          predictedCarCompany: carCompany,
          predictedCarModel:
            vehicleTypeKey === "car" || vehicleType === "car"
              ? carModel
              : carVersion,
          predictedYear: manufactureYear,
          predictedCarVersion: carVersion,
          vehicleInspectionOcrId,
          vehicleTypeKey,
          modelCode: carModel,
          numOfSeat,
          carType,
          barrelType,
          barrelTypeKey,
        });
        if (carCompany && carModel && carVersion && manufactureYear) {
          toast.success(t("upload.notification.success"), {
            position: "top-center",
            autoClose: 3000,
          });
        }
      } catch (error: any) {
        const messError = get(error, "response.data.ocrErr.message");

        const mess = get(
          error,
          "response.data.message",
          t("upload.notification.server-error"),
        );
        toast.error(messError ?? mess, {
          position: "top-center",
          autoClose: 3000,
        });
      } finally {
        setIsLoadImg(false);
      }
    },
  });

  const handleClose = (e: React.MouseEvent<HTMLLabelElement, MouseEvent>) => {
    e.stopPropagation();
    resetSelect();
    setFiles([]);
    if (!isBack && files2?.length > 0) {
      setFiles2([]);
    }
  };

  return (
    <>
      <section className="container">
        {!disabled ? (
          <div {...getRootProps({ className: "dropzone" })}>
            <input {...getInputProps()} disabled={disabled} />
            {files.length === 0 ? (
              <div className={classes.noFile}>
                <img src={FileAdd} alt=""></img>
                <Typography
                  variant="body2"
                  sx={{ color: "#5678ff", fontWeight: 600 }}
                >
                  {description}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color:
                      theme.palette.mode === "light"
                        ? "rgba(9, 30, 66, 0.6)"
                        : "white",
                  }}
                >
                  {t("form.registry.description")}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color:
                      theme.palette.mode === "light"
                        ? "rgba(9, 30, 66, 0.6)"
                        : "white",
                  }}
                >
                  {t("form.registry.file-format")}
                </Typography>
              </div>
            ) : (
              <>
                <div
                  className={
                    type === "two" && layout === "result"
                      ? classes.background2
                      : classes.background
                  }
                >
                  <img
                    src={files[0].preview}
                    className={classes.img}
                    alt=""
                    onLoad={() => {
                      URL.revokeObjectURL(files[0].preview);
                    }}
                  />

                  {isLoadImg && (
                    <div className="half-overlay">
                      <div className="half-overlay-1"></div>
                    </div>
                  )}

                  <div className={classes.iconClear}>
                    <IconButton
                      sx={{ color: "white" }}
                      aria-label="upload picture"
                      component="label"
                      onClick={(e) => {
                        handleClose(e);
                      }}
                    >
                      <HighlightOffIcon />
                    </IconButton>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="dropzoneDisabled">
            <div className={classes.noFile}>
              <img src={FileAdd} alt=""></img>
              <Typography
                variant="body2"
                sx={{ color: "#5678ff", fontWeight: 600 }}
              >
                {description}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color:
                    theme.palette.mode === "light"
                      ? "rgba(9, 30, 66, 0.6)"
                      : "white",
                }}
              >
                {t("form.registry.description")}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color:
                    theme.palette.mode === "light"
                      ? "rgba(9, 30, 66, 0.6)"
                      : "white",
                }}
              >
                {t("form.registry.file-format")}
              </Typography>
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default ImageUploadingFromLib;
